import cn from 'classnames'
import React, { forwardRef, memo, useState } from 'react'
import { useUpdateEffect } from 'react-use'

import styles from './VotesInput.module.scss'

import UpvoteIcon from '~icons/illustrations/upvote.svg'
import Spinner from '~shared/ui/Spinner'
import Text from '~shared/ui/Text'

interface VotesInputProps {
  value?: number
  defaulValue?: number
  className?: string
  readOnly?: boolean
  disabled?: boolean
  cantUpvote?: boolean
  cantDownvote?: boolean
  loading?: boolean
  onChange?: (value: number) => void
  onMouseEnter?: (ev: React.MouseEvent<HTMLDivElement>) => void
  onMouseLeave?: (ev: React.MouseEvent<HTMLDivElement>) => void
}

function VotesInput(
  {
    value,
    defaulValue,
    className,
    readOnly,
    disabled,
    cantUpvote = false,
    cantDownvote = false,
    loading,
    onChange,
    onMouseEnter,
    onMouseLeave,
    ...props
  }: VotesInputProps,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const [valueState, setValueState] = useState(() => defaulValue || value || 0)

  useUpdateEffect(() => {
    if (typeof value !== 'undefined') {
      setValueState(value)
    }
  }, [value])

  const handleOnChange = (ev: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled && !readOnly) {
      let nextValue = valueState

      if (ev.currentTarget.dataset.type === 'UPVOTE' && !cantUpvote) {
        nextValue += 1
      } else if (ev.currentTarget.dataset.type === 'DOWNVOTE' && !cantDownvote) {
        nextValue -= 1
      }

      if (typeof value === 'undefined') {
        setValueState(nextValue)
      }

      onChange?.(nextValue)
    }
  }

  return (
    <div
      ref={ref}
      className={cn(styles.root, disabled && styles.disabled, readOnly && styles.readOnly, className)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...props}
    >
      <div className={styles.inner}>
        <button
          type="button"
          data-type="UPVOTE"
          disabled={disabled || cantUpvote}
          className={styles.upvoteBtn}
          onClick={handleOnChange}
        >
          <UpvoteIcon />
        </button>

        <Text as="div" variant="default" textType="body-md" textAlign="center" className={styles.votes}>
          {Number.isInteger(valueState) && !Number.isNaN(valueState) ? valueState : 0}
        </Text>

        <button
          type="button"
          data-type="DOWNVOTE"
          disabled={disabled || cantDownvote}
          className={styles.downvoteBtn}
          onClick={handleOnChange}
        >
          <UpvoteIcon />
        </button>

        {loading && (
          <div className={styles.loader}>
            <div className={styles.overlay} />
            <div className={styles.spinner}>
              <Spinner variant="default" size="sm" />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default memo(forwardRef<HTMLDivElement, VotesInputProps>(VotesInput))
